import React, { useState, useEffect, useCallback } from 'react';
import { Table, Spinner, Alert, Badge, Button, ButtonGroup, Input, Label, FormGroup } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';
import SchedulerLog from '../../entities/SchedulerLog';

export const AdminSchedulerLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [selectedTag, setSelectedTag] = useState('');
  // Default to Information: shows Information, Warning and Error entries
  // (Debug is more verbose and excluded by default).
  const [selectedLevel, setSelectedLevel] = useState('Information');
  // Free-text message filter — partial, case-insensitive match against the
  // log Message column. Backed by Postgres ILIKE on the backend.
  // `messageFilter` is the controlled input value (updates on every keystroke);
  // `appliedMessageFilter` is the debounced value that `loadLogs` actually uses.
  const [messageFilter, setMessageFilter] = useState('');
  const [appliedMessageFilter, setAppliedMessageFilter] = useState('');

  // Pagination
  const [offset, setOffset] = useState(0);
  const [limit] = useState(100);

  // The Tag dropdown is exact-matched against the backend (see
  // `SchedulerLogRepository.SearchAsync`, which appends `"""Tag"" = @Tag"`).
  // Keep this list in sync with every `private const string LogTag` in
  // `WaveWise.WebApp/HostedServices/**` and any other service that calls
  // `SchedulerLogService.Log*Async(LogTag, ...)`. A tag missing from this
  // list will still appear under "All Jobs" but cannot be filtered to.
  const availableTags = [
    { value: '', label: 'All Jobs' },
    // Schedulers
    { value: 'candle-collection-scheduler', label: 'Candle Collection Scheduler' },
    { value: 'candle-collector', label: 'Candle Collector' },
    { value: 'model-execution-scheduler', label: 'Model Execution' },
    { value: 'market-breadth-scheduler', label: 'Market Breadth' },
    { value: 'market-breadth-weekly-scheduler', label: 'Market Breadth Weekly' },
    { value: 'bond-market-activity-scheduler', label: 'Bond Market Activity' },
    { value: 'ctrac-package-scheduler', label: 'CTRAC Package' },
    { value: 'trading-strategy-execution-scheduler', label: 'Trading Strategy Execution' },
    { value: 'realtime-candles-execution-scheduler', label: 'Realtime Candles Execution' },
    { value: 'performance-calculation-scheduler', label: 'Performance Calculation' },
    { value: 'tickers-validation-scheduler', label: 'Tickers Validation' },
    { value: 'trading-strategy-amount-adjustment-scheduler', label: 'Trading Strategy Amount Adjustment' },
    // Job Processor
    { value: 'job-processor', label: 'Job Processor' },
    // Job Processor Runners
    { value: 'bond-market-activity-collector', label: 'Bond Market Activity Collector' },
    { value: 'market-breadth-collector', label: 'Market Breadth Collector' },
    { value: 'market-breadth-weekly-collector', label: 'Market Breadth Weekly Collector' },
    { value: 'ctrac-package-generator', label: 'CTRAC Package Generator' },
    { value: 'trading-strategy-executor', label: 'Trading Strategy Executor' },
    { value: 'TickerCollection', label: 'Ticker Collection' },
    { value: 'backup', label: 'Backup' },
    { value: 'tickers-validation-job', label: 'Tickers Validation Job' },
    { value: 'trading-strategy-amount-adjuster', label: 'Trading Strategy Amount Adjuster' },
    // Other services that write to the scheduler log
    { value: 'analysis-package-service', label: 'Analysis Package Service' }
  ];

  // Levels ordered from least to most severe. The selected level is treated
  // as a minimum verbosity — entries at the selected level and every level
  // above (more severe) are returned.
  const availableLevels = [
    { value: 'Debug', label: 'Debug' },
    { value: 'Information', label: 'Information' },
    { value: 'Warning', label: 'Warning' },
    { value: 'Error', label: 'Error' }
  ];

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const searchParams = {
        Offset: offset,
        Limit: limit,
        SkipTotalCount: false
      };

      if (selectedTag) {
        searchParams.Tag = selectedTag;
      }

      // Always send the selected level. The backend treats it as a minimum
      // verbosity and returns the selected level plus every more-severe level.
      searchParams.Level = selectedLevel;

      if (appliedMessageFilter.trim()) {
        searchParams.MessageContains = appliedMessageFilter.trim();
      }

      const res = await apiClient.postJson('/api/scheduler-logs/search', searchParams);
      const response = await res.json();
      setLogs((response.Items || []).map((item) => new SchedulerLog(item)));
      setTotalCount(response.TotalCount || 0);
    } catch (err) {
      console.error('Failed to load scheduler logs:', err);
      setError(err.message || 'Failed to load scheduler logs');
    } finally {
      setLoading(false);
    }
  }, [offset, limit, selectedTag, selectedLevel, appliedMessageFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Debounce the message filter input: only commit it to `appliedMessageFilter`
  // (which drives the search) and reset pagination after the user has stopped
  // typing for 300ms. This avoids firing a request on every keystroke and
  // keeps the user on a valid page once the filter narrows the result set.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedMessageFilter(messageFilter);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [messageFilter]);

  const handleClearLogs = async (tag = null) => {
    if (!window.confirm(tag
      ? `Are you sure you want to clear all logs for tag "${tag}"?`
      : 'Are you sure you want to clear ALL scheduler logs?')) {
      return;
    }

    try {
      setLoading(true);
      const url = tag ? `/api/scheduler-logs?tag=${encodeURIComponent(tag)}` : '/api/scheduler-logs';
      const res = await apiClient.deleteJson(url);
      const result = await res.json();

      alert(`Successfully deleted ${result.DeletedCount} log entries`);
      await loadLogs();
    } catch (err) {
      console.error('Failed to clear logs:', err);
      alert(err.message || 'Failed to clear logs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOldLogs = async (days = 30) => {
    if (!window.confirm(`Are you sure you want to delete logs older than ${days} days?`)) {
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.deleteJson(`/api/scheduler-logs/old?daysToKeep=${days}`);
      const result = await res.json();

      alert(`Successfully deleted ${result.DeletedCount} old log entries`);
      await loadLogs();
    } catch (err) {
      console.error('Failed to delete old logs:', err);
      alert(err.message || 'Failed to delete old logs');
    } finally {
      setLoading(false);
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'Error':
        return 'danger';
      case 'Warning':
        return 'warning';
      case 'Information':
        return 'info';
      case 'Debug':
        return 'dark';
      default:
        return 'secondary';
    }
  };

  const getTagBadgeColor = (tag) => {
    if (tag.includes('candle')) return 'primary';
    if (tag.includes('model')) return 'success';
    if (tag.includes('job')) return 'info';
    return 'secondary';
  };

  const handlePreviousPage = () => {
    setOffset(Math.max(0, offset - limit));
  };

  const handleNextPage = () => {
    if (offset + limit < totalCount) {
      setOffset(offset + limit);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Scheduler Logs</h2>
        <div>
          <Button color="primary" onClick={loadLogs} disabled={loading} className="me-2">
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded">
        <div className="row">
          <div className="col-md-3">
            <FormGroup>
              <Label for="tagFilter">Filter by Job</Label>
              <Input
                type="select"
                id="tagFilter"
                value={selectedTag}
                onChange={(e) => {
                  setSelectedTag(e.target.value);
                  setOffset(0);
                }}
              >
                {availableTags.map(tag => (
                  <option key={tag.value} value={tag.value}>{tag.label}</option>
                ))}
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup>
              <Label for="levelFilter">Filter by Level</Label>
              <Input
                type="select"
                id="levelFilter"
                value={selectedLevel}
                onChange={(e) => {
                  setSelectedLevel(e.target.value);
                  setOffset(0);
                }}
              >
                {availableLevels.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup>
              <Label for="messageFilter">Filter by Message</Label>
              <div className="d-flex">
                <Input
                  type="text"
                  id="messageFilter"
                  placeholder="Partial, case-insensitive"
                  value={messageFilter}
                  onChange={(e) => setMessageFilter(e.target.value)}
                />
                {messageFilter && (
                  <Button
                    color="secondary"
                    size="sm"
                    className="ms-2"
                    onClick={() => setMessageFilter('')}
                    title="Clear message filter"
                  >
                    ×
                  </Button>
                )}
              </div>
            </FormGroup>
          </div>
          <div className="col-md-3">
            <Label>Clear Logs</Label>
            <div>
              <ButtonGroup>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleClearLogs(selectedTag || null)}
                  disabled={loading}
                >
                  {selectedTag ? 'Clear Filtered Logs' : 'Clear All Logs'}
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => handleDeleteOldLogs(30)}
                  disabled={loading}
                >
                  Delete Logs &gt; 30 days
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => handleDeleteOldLogs(7)}
                  disabled={loading}
                >
                  Delete Logs &gt; 7 days
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </div>
      </div>

      {error && <Alert color="danger" fade={false}>{error}</Alert>}

      {loading && <Spinner color="primary" />}

      {!loading && logs.length === 0 && (
        <Alert color="info" fade={false}>No scheduler logs found</Alert>
      )}

      {!loading && logs.length > 0 && (
        <>
          <div className="mb-2">
            <strong>Total logs: {totalCount}</strong>
            {' - '}
            Showing {offset + 1} to {Math.min(offset + limit, totalCount)}
          </div>

          <div className="mb-3">
            <ButtonGroup>
              <Button
                color="secondary"
                size="sm"
                onClick={handlePreviousPage}
                disabled={offset === 0}
              >
                Previous
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={handleNextPage}
                disabled={offset + limit >= totalCount}
              >
                Next
              </Button>
            </ButtonGroup>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th style={{ width: '180px' }}>Timestamp</th>
                  <th style={{ width: '200px' }}>Job</th>
                  <th style={{ width: '100px' }}>Level</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.Id}>
                    <td>{log.getFormattedTimestamp()}</td>
                    <td>
                      <Badge color={getTagBadgeColor(log.Tag)}>
                        {log.Tag}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={getLevelBadgeColor(log.Level)}>
                        {log.Level}
                      </Badge>
                    </td>
                    <td>
                      <div style={{ maxWidth: '600px' }}>
                        <div>{log.Message}</div>
                        {log.Exception && (
                          <details className="mt-2">
                            <summary style={{ cursor: 'pointer', color: '#dc3545' }}>
                              Exception Details
                            </summary>
                            <pre className="mt-2 p-2 bg-light" style={{
                              fontSize: '0.8rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {log.Exception}
                            </pre>
                          </details>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="mb-3">
            <ButtonGroup>
              <Button
                color="secondary"
                size="sm"
                onClick={handlePreviousPage}
                disabled={offset === 0}
              >
                Previous
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={handleNextPage}
                disabled={offset + limit >= totalCount}
              >
                Next
              </Button>
            </ButtonGroup>
          </div>
        </>
      )}
    </div>
  );
};
